import React, { useEffect, useRef } from "react";
import * as THREE from "three";

const ThreeBackground = () => {
  const hostRef = useRef(null);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return undefined;

    const scene = new THREE.Scene();
    const camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.5));
    renderer.setSize(window.innerWidth, window.innerHeight);
    host.appendChild(renderer.domElement);

    const gradientMaterial = new THREE.ShaderMaterial({
      transparent: true,
      uniforms: {
        uTime: { value: 0 },
        uResolution: { value: new THREE.Vector2(window.innerWidth, window.innerHeight) }
      },
      vertexShader: `
        varying vec2 vUv;
        void main() {
          vUv = uv;
          gl_Position = vec4(position.xy, 0.0, 1.0);
        }
      `,
      fragmentShader: `
        precision mediump float;
        varying vec2 vUv;
        uniform float uTime;
        uniform vec2 uResolution;

        float glow(vec2 uv, vec2 center, float radius) {
          float d = distance(uv, center);
          return smoothstep(radius, 0.0, d);
        }

        float ringLines(vec2 uv, vec2 center, float scale, float thickness) {
          float d = length((uv - center) * vec2(1.0, 1.34));
          float rings = abs(sin(d * scale));
          return smoothstep(thickness, 0.0, rings) * smoothstep(0.72, 0.05, d);
        }

        float diagonalLines(vec2 uv, float offset) {
          float line = fract((uv.x + uv.y * 0.92 + offset) * 18.0);
          return smoothstep(0.03, 0.0, line) * 0.45;
        }

        void main() {
          vec2 uv = vUv;
          float t = uTime * 0.08;

          vec3 base = mix(vec3(0.985, 0.988, 0.992), vec3(0.84, 0.90, 0.97), uv.y * 0.82 + uv.x * 0.18);
          float red = glow(uv, vec2(0.04 + sin(t) * 0.018, 0.84 + cos(t * 1.2) * 0.018), 0.46);
          float blue = glow(uv, vec2(0.9 + cos(t * 1.1) * 0.02, 0.22 + sin(t) * 0.02), 0.62);
          float whiteBloom = glow(uv, vec2(0.7, 0.82), 0.4);

          float leftRings = ringLines(uv, vec2(0.02 + sin(t) * 0.015, 0.82), 92.0, 0.08);
          float rightRings = ringLines(uv, vec2(1.05 + cos(t) * 0.012, 0.88), 98.0, 0.08);
          float lowerRings = ringLines(uv, vec2(0.0, 0.08), 80.0, 0.07);
          float slashes = diagonalLines(uv, t * 0.16);

          vec3 color = base;
          color = mix(color, vec3(1.0, 0.88, 0.9), red * 0.55);
          color = mix(color, vec3(0.68, 0.8, 0.96), blue * 0.55);
          color = mix(color, vec3(1.0), whiteBloom * 0.32);
          color += vec3(0.98, 1.0, 1.0) * (leftRings + rightRings + lowerRings) * 0.22;
          color += vec3(0.9, 0.96, 1.0) * slashes * 0.12;

          float grain = fract(sin(dot(uv * uResolution + uTime, vec2(12.9898, 78.233))) * 43758.5453);
          color += (grain - 0.5) * 0.012;

          gl_FragColor = vec4(color, 1.0);
        }
      `
    });

    const plane = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), gradientMaterial);
    scene.add(plane);

    const particleCount = 70;
    const positions = new Float32Array(particleCount * 3);
    for (let index = 0; index < particleCount; index += 1) {
      positions[index * 3] = Math.random() * 2 - 1;
      positions[index * 3 + 1] = Math.random() * 2 - 1;
      positions[index * 3 + 2] = 0;
    }

    const particleGeometry = new THREE.BufferGeometry();
    particleGeometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));
    const particleMaterial = new THREE.PointsMaterial({
      color: 0xffffff,
      size: 0.008,
      transparent: true,
      opacity: 0.18,
      sizeAttenuation: false
    });
    const particles = new THREE.Points(particleGeometry, particleMaterial);
    scene.add(particles);

    let frameId;
    const clock = new THREE.Clock();

    const render = () => {
      const elapsed = clock.getElapsedTime();
      gradientMaterial.uniforms.uTime.value = elapsed;
      particles.rotation.z = elapsed * 0.018;
      particles.position.x = Math.sin(elapsed * 0.12) * 0.025;
      renderer.render(scene, camera);
      frameId = window.requestAnimationFrame(render);
    };

    const resize = () => {
      renderer.setSize(window.innerWidth, window.innerHeight);
      gradientMaterial.uniforms.uResolution.value.set(window.innerWidth, window.innerHeight);
    };

    window.addEventListener("resize", resize);
    render();

    return () => {
      window.cancelAnimationFrame(frameId);
      window.removeEventListener("resize", resize);
      particleGeometry.dispose();
      particleMaterial.dispose();
      plane.geometry.dispose();
      gradientMaterial.dispose();
      renderer.dispose();
      renderer.domElement.remove();
    };
  }, []);

  return <div className="three-bg" ref={hostRef} aria-hidden="true" />;
};

export default ThreeBackground;
